import type { PiiSpan, PiiType } from "./types";
import { detectStructured } from "./patterns";
import { numberSpans } from "./coref";
import { dropStandalonePlaceNames } from "./placeNames";

/**
 * Orchestrates the detection layers into a single, de-overlapped, whitelist-
 * filtered, entity-numbered list of spans (LAR-27 v2). Pure and synchronous so
 * it is trivially unit-testable; the async ScandiNER pass runs in the Web
 * Worker (`piiScanWorker.ts`) and injects its spans via `options.nerSpans`.
 * Names/addresses/orgs therefore only appear when a NER pass ran — the v1
 * gazetteer lists are gone (plan decision 1); the user's manual
 * highlight-to-classify in the review dialog is the NER-miss backstop.
 */

export interface DetectOptions {
  /**
   * Per-tenant whitelist of approved terms (normalised: trimmed, lower-cased).
   * Suppresses matching `soft` spans only — never `hard` identifiers
   * (plan decision 6). Fetched from the API and passed in by the caller.
   */
  whitelist?: ReadonlySet<string>;
  /**
   * Spans produced by the async NER layer (`detectNer`), merged with the regex
   * layer before overlap resolution. Omitted in contexts with no model (unit
   * tests, regex-only fallbacks).
   */
  nerSpans?: readonly PiiSpan[];
}

/** Tie-break priority when two spans start at the same place and length. */
const TYPE_PRIORITY: Record<PiiType, number> = {
  email: 7,
  cpr: 6,
  account: 5,
  phone: 4,
  address: 3,
  name: 2,
  org: 1,
  custom: 0,
};

/** Keep the earliest, then longest, then highest-priority span among overlaps. */
function resolveOverlaps(spans: PiiSpan[]): PiiSpan[] {
  const sorted = [...spans].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const lenDiff = b.end - b.start - (a.end - a.start);
    if (lenDiff !== 0) return lenDiff;
    return TYPE_PRIORITY[b.type] - TYPE_PRIORITY[a.type];
  });

  const kept: PiiSpan[] = [];
  let lastEnd = -1;
  for (const span of sorted) {
    if (span.start >= lastEnd) {
      kept.push(span);
      lastEnd = span.end;
    }
  }
  return kept;
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Detect PII in `text`. Returns spans sorted by position, overlaps resolved,
 * whitelisted soft spans removed (hard identifiers are never suppressed), and
 * `entityId`/`inflection` assigned so coreferent mentions share a numbered
 * placeholder (see `numberSpans`).
 */
export function detect(text: string, options: DetectOptions = {}): PiiSpan[] {
  if (!text) return [];
  // Bare place names are dropped from the NER layer only (LAR-367 #5b), so a
  // hard identifier or a manual highlight can never be suppressed this way.
  const ner = dropStandalonePlaceNames(text, options.nerSpans ?? []);
  const all = [...detectStructured(text), ...ner];
  const resolved = resolveOverlaps(all);

  const whitelist = options.whitelist;
  const filtered =
    !whitelist || whitelist.size === 0
      ? resolved
      : resolved.filter(
          (span) => span.tier === "hard" || !whitelist.has(normalize(span.text))
        );

  // Number last, over the final span set, so the placeholders match exactly what
  // the user reviews and what redaction emits.
  return numberSpans(filtered);
}
